// Global Instructions Rule Applied!

import { z } from 'zod'
import { supabase } from '../lib/supabase'

export function ensureRowKeys(
  data: Record<string, unknown>[],
  fallbackKeyField?: string
): (Record<string, unknown> & { key: string })[] {
  return data.map((row, idx) => {
    const key =
      (fallbackKeyField && row[fallbackKeyField] != null ? String(row[fallbackKeyField]) : null) ||
      `row-${idx}`
    return { ...row, key }
  })
}

export const formatKeyToLabel = (
  key: string,
  isSingular: boolean = true,
  preserveTablePrefix: boolean = false
): string => {
  // First, handle keys with "__" separator
  let processedKey = key
  if (key.includes('__')) {
    const parts = key.split('__')
    if (parts.length > 1) {
      if (preserveTablePrefix) {
        // Replace "__" with space to keep both table prefix and field name
        processedKey = parts.join(' ')
      } else {
        // Take everything after the first "__"
        processedKey = parts.slice(1).join('__')
      }
    }
  }

  // Then, handle keys with "." separator - take the last part
  if (processedKey.includes('.')) {
    const parts = processedKey.split('.')
    processedKey = parts[parts.length - 1]
  }

  // Replace single underscores with spaces
  let formatted = processedKey.replace(/_/g, ' ')

  // Split by spaces and filter out empty strings
  const words = formatted.split(/\s+/).filter((word) => word.length > 0)

  // Proper case each word (first letter uppercase, rest lowercase)
  const properCasedWords = words.map((word) => {
    if (word.length === 0) return word
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  })

  // Remove words that contain "id" or "code" (case insensitive)
  const filteredWords = properCasedWords.filter((word) => {
    const lowerWord = word.toLowerCase()
    return !lowerWord.includes('id') && !lowerWord.includes('code')
  })

  // Handle singular/plural based on isSingular prop
  if (filteredWords.length > 0) {
    // Handle table prefix (first word) when preserveTablePrefix is true
    if (preserveTablePrefix && filteredWords.length > 1) {
      const firstWord = filteredWords[0]
      if (isSingular) {
        // Remove trailing 's' from table prefix if present
        filteredWords[0] = firstWord.endsWith('s') ? firstWord.slice(0, -1) : firstWord
      } else {
        // Add 's' to table prefix if not already present
        filteredWords[0] = firstWord.endsWith('s') ? firstWord : firstWord + 's'
      }
    }

    // Handle last word (field name)
    const lastWord = filteredWords[filteredWords.length - 1]
    if (isSingular) {
      // Remove trailing 's' if present
      filteredWords[filteredWords.length - 1] = lastWord.endsWith('s') ? lastWord.slice(0, -1) : lastWord
    } else {
      // Add 's' if not already present
      filteredWords[filteredWords.length - 1] = lastWord.endsWith('s') ? lastWord : lastWord + 's'
    }
  }

  return filteredWords.join(' ')
}

const getNestedValue = (obj: any, path: string): any => {
  if (!path || !obj) return undefined
  const parts = path.split('.')
  let current = obj
  for (const part of parts) {
    if (current == null) return undefined
    current = current[part]
  }
  return current
}

/**
 * Resolve a table value path by trying exact key match first, then dotted-path traversal.
 * This supports payloads that may encode dotted fields as literal keys.
 */
function getTableValue(record: any, valuePath: string): any {
  if (!record || !valuePath) return undefined
  if (Object.prototype.hasOwnProperty.call(record, valuePath)) {
    return record[valuePath]
  }
  return getNestedValue(record, valuePath)
}

/**
 * Reporting `module_key` `rounds` often uses synthetic `value_key`s (`valuation`, `latest_round`, …).
 * Resolve them for read-only cells: deals use flat `rounds__*` / `prog__valuation`; companies use
 * `prog__recent_round` / `prog__latest_round` (same semantics as companiesTableMapper).
 */
export function getTableCellValueForRecord(
  record: any,
  valuePath: string | undefined,
  moduleKey: string | undefined
): any {
  if (!valuePath || !record) return undefined

  if (record.accounts__id != null && valuePath === 'funding_stage') {
    const latest = getNestedValue(record, 'prog__latest_round')
    const fromNested =
      latest?.rounds__funding_stage__label ?? latest?.rounds__funding_stage
    if (fromNested !== undefined && fromNested !== null && fromNested !== '') {
      return fromNested
    }
  }

  const direct = getTableValue(record, valuePath)
  if (direct !== undefined && direct !== null && direct !== '') {
    return direct
  }

  // Companies grid `role` column uses `prog__account_roles`; there is no top-level `role` field on RPC rows.
  if (record.accounts__id != null && valuePath === 'role') {
    const roles = record.prog__account_roles
    if (Array.isArray(roles) && roles.length > 0) {
      const ids = roles
        .map((r: { account_roles__account_role_choice_id?: string } | string) => {
          if (typeof r === 'string') return r
          if (r && typeof r === 'object' && r.account_roles__account_role_choice_id) {
            return r.account_roles__account_role_choice_id
          }
          return null
        })
        .filter((id: string | null): id is string => id != null && id !== '')
      if (ids.length > 0) return ids
    }
  }

  // Reporting configs may reference account business address fields using alias keys.
  if (record.accounts__id != null) {
    if (
      valuePath === 'business_city' ||
      valuePath === 'accounts__business_city_code' ||
      valuePath === 'accounts__business_city_code__label'
    ) {
      return getNestedValue(record, 'accounts__business_city_label') ?? direct
    }
    if (
      valuePath === 'business_country' ||
      valuePath === 'accounts__business_country_code' ||
      valuePath === 'accounts__business_country_code__label'
    ) {
      return (
        getNestedValue(record, 'accounts__business_country_label') ??
        getNestedValue(record, 'accounts__business_country_code__label') ??
        direct
      )
    }
  }

  if (record.deals__id != null && moduleKey === 'rounds') {
    switch (valuePath) {
      case 'latest_round':
      case 'valuation_round':
        return getNestedValue(record, 'rounds__name')
      case 'valuation':
        return getNestedValue(record, 'prog__valuation')
      case 'funding_stage':
        return (
          getNestedValue(record, 'rounds__funding_stage__label') ?? getNestedValue(record, 'rounds__funding_stage')
        )
      default:
        return direct
    }
  }

  const companyUsesNestedRound =
    record.accounts__id != null &&
    (moduleKey === 'rounds' ||
      (typeof valuePath === 'string' &&
        (valuePath.startsWith('rounds__') ||
          valuePath === 'latest_round' ||
          valuePath === 'valuation_round' ||
          valuePath === 'valuation' ||
          valuePath === 'funding_stage')))

  if (companyUsesNestedRound) {
    const recent = getNestedValue(record, 'prog__recent_round')
    const latest = getNestedValue(record, 'prog__latest_round')
    switch (valuePath) {
      case 'latest_round':
        return latest?.rounds__name
      case 'valuation_round':
        return recent?.rounds__name
      case 'valuation': {
        const post = recent?.rounds__post_money_valuation
        const pre = recent?.rounds__pre_money_valuation
        return post ?? pre ?? direct
      }
      case 'funding_stage':
        return latest?.rounds__funding_stage__label ?? latest?.rounds__funding_stage ?? direct
      case 'rounds__raise_amount':
        return latest?.rounds__raise_amount ?? direct
      case 'rounds__security_type':
        return latest?.rounds__security_type__label ?? latest?.rounds__security_type ?? direct
      default:
        return direct
    }
  }

  return direct
}

/**Creates a frozen object mapping schema keys to themselves for type-safe field access*/
export function makeFieldKeys<S extends z.ZodRawShape>(schema: z.ZodObject<S>) {
  const entries = Object.keys(schema.shape).map((k) => [k, k] as const)
  return Object.freeze(Object.fromEntries(entries)) as { [K in keyof S & string]: K }
}

/**
 * Validates data with Zod schema while preserving null values
 * TypeScript equivalent of Python's validate_with_zon_schema function
 * @param schema - Zod schema to validate against
 * @param data - Raw data to validate
 * @param validateContext - Context string for logging which model is being validated
 * @param options - Validation options
 * @returns Validated data
 */
export function validateWithZodSchema<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  validateContext: string,
  options: { isArray: true; validationEnabledEnv?: string }
): z.infer<T>[]
export function validateWithZodSchema<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  validateContext: string,
  options?: { isArray?: false; validationEnabledEnv?: string }
): z.infer<T>
export function validateWithZodSchema<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  validateContext: string,
  options?: { isArray?: boolean; validationEnabledEnv?: string }
): z.infer<T> | z.infer<T>[]
export function validateWithZodSchema<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  validateContext: string,
  options: {
    isArray?: boolean
    validationEnabledEnv?: string
  } = {}
): z.infer<T> | z.infer<T>[] {
  const { isArray = false, validationEnabledEnv = 'REACT_APP_VALIDATE_REQUEST_DATA' } = options

  if (process.env[validationEnabledEnv] !== 'true') {
    return data as z.infer<T> | z.infer<T>[]
  }

  try {
    if (isArray) {
      if (!Array.isArray(data)) {
        throw new Error(`${validateContext} validation failed: Expected array, got ${typeof data}`)
      }
      return z.array(schema).parse(data) as z.infer<T>[]
    } else {
      return schema.parse(data) as z.infer<T>
    }
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMsg = `${validateContext} validation failed: ${error.issues.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ')}`
      console.error(errorMsg)
      throw new Error(errorMsg)
    }
    console.error(`${validateContext} validation failed:`, error)
    throw error
  }
}

/**
 * Get authentication headers with JWT token
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session }
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('No authentication token available')
  }

  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json'
  }
}

/**
 * Generic factory function to create async thunks for data fetching
 * Follows the pattern of calling an API function that returns { data, error? }
 */
// export function createDataFetchThunk<TData, TArgs = void>(
//   typePrefix: string,
//   apiFn: (args: TArgs) => Promise<{ data: TData; error?: string }>
// ) {
//   return createAsyncThunk(
//     typePrefix,
//     async (args: TArgs, { rejectWithValue }) => {
//       try {
//         const response = await apiFn(args)
//         if (response.error) {
//           return rejectWithValue(response.error)
//         }
//         return response.data
//       } catch (error) {
//         return rejectWithValue(error instanceof Error ? error.message : 'Request failed')
//       }
//     }
//   )
// }

/**
 * Generic helper to add async thunk cases to a Redux slice builder
 * Handles standard loading/error states, with custom fulfilled handler
 */
// export function addAsyncThunkCases(builder, thunk, fulfilledHandler) {
//   builder
//     .addCase(thunk.pending, (state) => {
//       state.loading = true
//       state.error = null
//     })
//     .addCase(thunk.fulfilled, fulfilledHandler)
//     .addCase(thunk.rejected, (state, action) => {
//       state.loading = false
//       state.error = action.payload
//     })
// }

/**
 * One sentence for a toast from one or more `{fieldKey: message}` validation
 * maps, e.g. "Please complete the required fields: Name, Company".
 *
 * The InputManager kit already paints the per-field red text; this is the
 * companion signal for when those fields are scrolled out of view (a sticky
 * FormSection header hides the top rows of a long drawer). Messages follow the
 * `validateRequiredFields` shape "<Label> is required", so the label is what
 * remains once that suffix is stripped. Returns null when there is nothing to
 * report.
 */
export const summariseFieldErrors = (
  errorMaps: Array<Record<string, string> | undefined | null>,
  prefix = 'Please complete the required fields'
): string | null => {
  const labels: string[] = []
  errorMaps.forEach((errors) => {
    if (!errors) return
    Object.values(errors).forEach((message) => {
      if (typeof message !== 'string') return
      const label = message.replace(/ is required$/i, '').trim()
      if (label && !labels.includes(label)) labels.push(label)
    })
  })
  if (labels.length === 0) return null
  return `${prefix}: ${labels.join(', ')}`
}

/**
 * Parse a response body as JSON, or null when it is empty or not JSON (a 204,
 * an HTML error page, a truncated body). The type argument states the shape
 * the caller expects — the body is not validated against it.
 */
export const readJsonBody = async <T = unknown>(response: Response): Promise<T | null> => {
  try {
    return await response.json()
  } catch {
    return null
  }
}

/**
 * The human-readable message behind a failed backend Response.
 *
 * handle_route_custom_error puts the sentence in `message`; handle_route_error
 * (500) puts the generic "Internal server error" in `message` and the real
 * text in `error`. Prefer the specific one, fall back to `fallback` + status so
 * the caller never has to show a bare "500 Internal Server Error".
 * Safe to call after the body has already been parsed — pass `body`.
 */
export const readBackendErrorMessage = async (
  response: Response,
  fallback: string,
  body?: unknown
): Promise<string> => {
  let parsed: unknown = body
  if (parsed === undefined) {
    parsed = await readJsonBody(response)
  }
  const envelope = (parsed && typeof parsed === 'object' ? parsed : {}) as {
    message?: unknown
    error?: unknown
  }
  const message = typeof envelope.message === 'string' ? envelope.message.trim() : ''
  const detail = typeof envelope.error === 'string' ? envelope.error.trim() : ''
  if (detail && (!message || message === 'Internal server error')) return detail
  if (message) return message
  return `${fallback}: ${response.status} ${response.statusText}`.trim()
}
